import {Helmet} from '@dr.pogodin/react-helmet';
import {Form, FormField, FormSubmitHandler} from 'platform/components';
import {Box, Space, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useParams} from 'react-router-dom';

import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useGetVehicleClassQuery,
  usePatchVehicleClassMutation,
  usePostVehicleClassMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

export function WorkshopVehicleClassDetail() {
  const {id} = useParams();
  const navigate = useNavigate();

  const [postVehicleClass] = usePostVehicleClassMutation();
  const [patchVehicleClass] = usePatchVehicleClassMutation();

  const {
    data: vehicleClass,
    isLoading,
    isError,
  } = useGetVehicleClassQuery({vehicleClassId: id as string}, {skip: isNilOrEmpty(id)});

  const header: SettingsTemplateHeader = {
    title: id ? (vehicleClass?.name ?? '') : i18n.t('entity.workshop.actions.newVehicleClass'),
    breadcrumbs: [
      {
        label: i18n.t('entity.workshop.labels.vehicleClasses'),
        href: settingsRoutes.workshopVehicleClasses,
      },
    ],
  };

  const handleNavigateBack = () => {
    navigate(settingsRoutes.workshopVehicleClasses);
  };

  const handleSubmit: FormSubmitHandler<WorkshopVehicleClassForm> = async (
    formValues,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const request = id
      ? patchVehicleClass({
          vehicleClassId: id,
          body: {
            name: formValues.name,
          },
        })
      : postVehicleClass({
          name: formValues.name,
        });

    return await request
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        handleNavigateBack();
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues = {
    name: vehicleClass?.name,
  };

  const titleKey = id
    ? 'entity.workshop.labels.vehicleClasses'
    : 'entity.workshop.actions.newVehicleClass';

  return (
    <>
      <Helmet title={i18n.t(titleKey)} />
      <SettingsTemplate
        header={header}
        isLoading={isLoading}
        isError={isError}
        isCreating={!id}
        data-testid={testIds.settings.workshopVehicleClassDetail('template')}
      >
        <Form<WorkshopVehicleClassForm>
          experimentalZodSchema={WorkshopVehicleClassFormSchema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          shouldWatchForUnsavedChanges
        >
          {(control, _formApi, navigateWithoutBlocker) => (
            <VStack spacing={4}>
              <Box width={100}>
                <FormField
                  type="text"
                  name="name"
                  label={i18n.t('general.labels.name')}
                  control={control}
                  isRequired
                  data-testid={testIds.settings.workshopVehicleClassDetail('name')}
                />
              </Box>
              <Space fillAvailable />
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    variant: 'secondary',
                    title: id
                      ? i18n.t('general.actions.discardChanges')
                      : i18n.t('general.actions.discard'),
                    onClick: () => navigateWithoutBlocker(settingsRoutes.workshopVehicleClasses),
                    'data-testid': testIds.settings.workshopVehicleClassDetail('discard'),
                  },
                  {
                    type: 'form-button',
                    control,
                    buttonType: 'submit',
                    variant: 'primary',
                    title: id
                      ? i18n.t('general.actions.saveChanges')
                      : i18n.t('general.actions.save'),
                    'data-testid': testIds.settings.workshopVehicleClassDetail('submit'),
                  },
                ]}
              />
            </VStack>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}

export const WorkshopVehicleClassFormSchema = z.object({
  name: z.string(),
});

export type WorkshopVehicleClassForm = z.infer<typeof WorkshopVehicleClassFormSchema>;
